import type {
  CmmdBoardTask,
  CmmdMember,
  CmmdProjectAgentMember,
  CmmdProjectTaskAssignmentSubject,
  UpdateCmmdBoardTaskParams,
} from "@cmmd-forge/contracts";
import type { ReactNode } from "react";

import { ProjectTaskAssigneesField } from "./ProjectTaskAssigneesField";
import { ProjectTaskPicker } from "./ProjectTaskPicker";
import { assigneeOptions, ownerOptions } from "./projectTaskAssignmentOptions";
import {
  type AssignmentOperation,
  assignmentSelectionValues,
  planAssignmentSelectionChange,
} from "./projectTaskAssignmentSelection";

export interface ProjectTaskPeopleFieldsProps {
  readonly task: CmmdBoardTask;
  readonly members: readonly CmmdMember[];
  readonly agentMembers: readonly CmmdProjectAgentMember[];
  readonly agentMembersLoading?: boolean | undefined;
  readonly agentMembersError?: string | null | undefined;
  readonly pending: boolean;
  readonly detail: (props: { readonly label: string; readonly children: ReactNode }) => ReactNode;
  readonly onUpdate: (id: number, patch: UpdateCmmdBoardTaskParams) => Promise<boolean>;
  readonly onSetAssignment: (
    id: number,
    subject: CmmdProjectTaskAssignmentSubject,
    operation?: AssignmentOperation,
  ) => Promise<boolean>;
}

/** Owner and Assignees — the two people-shaped rows of the task detail rail. */
export function ProjectTaskPeopleFields(props: ProjectTaskPeopleFieldsProps) {
  const Detail = props.detail;
  const selected = assignmentSelectionValues(props.task);

  return (
    <>
      <Detail label="Owner">
        <ProjectTaskPicker
          ariaLabel="Owner"
          presentation="quietField"
          value={props.task.ownerId === null ? null : String(props.task.ownerId)}
          emptyLabel="No owner"
          options={ownerOptions(props.task, props.members)}
          disabled={props.pending}
          onValueChange={(value) =>
            void props.onUpdate(props.task.id, {
              ownerId: value === null ? null : Number(value),
            })
          }
        />
      </Detail>
      <Detail label="Assignees">
        <ProjectTaskAssigneesField
          ariaLabel="Assignees"
          values={selected}
          emptyLabel="Unassigned"
          options={assigneeOptions(props.task, props.members, props.agentMembers)}
          disabled={props.pending || props.agentMembersLoading === true}
          onToggle={(value) => {
            // One chip moved, so the plan is one or two scoped writes — never a
            // replace, which would drop every assignee the user did not touch.
            for (const step of planAssignmentSelectionChange(selected, value)) {
              void props.onSetAssignment(props.task.id, step.subject, step.operation);
            }
          }}
        />
        {props.agentMembersError ? (
          <span className="sr-only">Agent Members unavailable: {props.agentMembersError}</span>
        ) : null}
      </Detail>
    </>
  );
}
